import React from 'react';
import s from '../../styles/renewal/Hero.module.css';
import p from '../../styles/renewal/OnePage.module.css';
import { HERO_POINTS, HERO_STATS } from './data';
import { noTranslate } from './formatters';

/** 라벨 옆 ⓘ — 마우스를 올리거나 탭(포커스)하면 설명 말풍선이 뜬다 */
const Info = ({ text, end }) => (
  <button
    type="button"
    className={`${s.info} ${end ? s.infoEnd : ''}`}
    aria-label={text}
    data-tip={text}
  >
    i
  </button>
);

const Hero = ({ estimate, onNavigate }) => (
  <section className={s.hero}>
    <div className={s.stage}>
      <img
        className={s.bg}
        src={`${process.env.PUBLIC_URL}/renewal/hero-bg.webp`}
        alt=""
        aria-hidden="true"
      />
      <div className={s.scrim} />

      <div className={s.inner}>
        <div className={s.grid}>
          <div className={s.copy}>
            <div className={`${p.badge} ${p.badgeLight}`} translate="no">BUSINESS BNPL</div>

            {/* 줄(line)은 항상 끊고, 줄 안에서는 어절 묶음(phrase) 사이에서만 꺾이게 한다. */}
            <h1 className={s.h1}>
              <span className={s.line}>사업에 필요한 결제,</span>
              <span className={s.line}>
                <span className={s.phrase}>한 번에</span>{' '}
                <span className={s.phrase}>부담하지 않고</span>
              </span>
              <span className={s.line}>
                <span className={s.phrase}>계약에 따라</span>{' '}
                <span className={s.phrase}>나누어 납부하세요.</span>
              </span>
            </h1>

            {/* 의미 묶음 사이에서만 꺾이게 해 '납부할 / 수 있는' 같은 끊김을 막는다. */}
            <p className={s.sub}>
              <span className={s.phrase} translate="no">오퍼그룹 BNPL은</span>{' '}
              <span className={s.phrase}>사업에 필요한 결제금액을</span>{' '}
              <span className={s.phrase}>계약에 따라</span>{' '}
              <span className={s.phrase}>나누어 납부할 수 있는</span>{' '}
              <span className={s.phrase}>사업자 전용 서비스입니다.</span>
            </p>

            <div className={s.points}>
              {HERO_POINTS.map((text) => (
                <div className={s.point} key={text}>
                  <span className={s.check} aria-hidden="true">
                    ✓
                  </span>
                  {text}
                </div>
              ))}
            </div>

            <div className={s.ctaRow}>
              <button
                type="button"
                className={`${p.btn} ${p.btnPrimary}`}
                onClick={() => onNavigate('contact')}
              >
                상담신청 →
              </button>
              <button
                type="button"
                className={`${p.btn} ${p.btnGhost}`}
                onClick={() => onNavigate('about')}
                translate="no"
              >
                BNPL 알아보기 ↘
              </button>
            </div>
          </div>

          {/* 예상 납부금액 카드 */}
          <div className={s.quote}>
            <div className={s.quoteTitle}>예상 납부금액을 간편하게 확인하세요.</div>
            <p className={s.quoteLead}>
              월평균 매출과 이익률만 입력하면 1년 기준 예상 이용금액과 월 납부금액을 확인할 수
              있습니다.
            </p>

            <div className={s.pair}>
              <div>
                <div className={s.fieldLabel}>
                  <label htmlFor="hero-sales">월평균 온라인·카드매출</label>
                  <Info text="최근 월평균 온라인 매출과 카드매출을 합한 금액입니다." />
                </div>
                <div className={s.field}>
                  <input
                    id="hero-sales"
                    className={s.input}
                    value={estimate.salesVal}
                    onChange={estimate.onSales}
                    inputMode="numeric"
                  />
                  <span className={s.unit}>만원</span>
                </div>
              </div>
              <div>
                <div className={s.fieldLabel}>
                  <label htmlFor="hero-margin">매출 대비 이익률</label>
                  <Info end text="매출에서 매입·필요경비를 뺀 비율입니다. 정확히 모르시면 대략적인 값으로 입력해 주세요." />
                </div>
                <div className={s.field}>
                  <input
                    id="hero-margin"
                    className={s.input}
                    value={estimate.marginVal}
                    onChange={estimate.onMargin}
                    inputMode="numeric"
                  />
                  <span className={s.unit}>%</span>
                </div>
              </div>
            </div>

            <div className={s.result}>
              <div className={s.summary}>
                <div className={s.summaryCell}>
                  <div className={s.resultLabel}>
                    1년 매출액 (예상)
                    <Info text="월평균 매출에 12개월을 곱한 금액입니다." />
                  </div>
                  <div className={`${s.summaryValue} ${s.accent}`}>{estimate.yearSalesLabel}</div>
                  <div className={s.summaryNote}>{estimate.yearSalesNote}</div>
                </div>
                <div className={s.summaryCell}>
                  <div className={s.resultLabel}>
                    이익률 금액 (예상)
                    <Info end text="1년 매출액에 매출 대비 이익률을 곱한 금액입니다." />
                  </div>
                  <div className={s.summaryValue}>{estimate.profitLabel}</div>
                  <div className={s.summaryNote}>{estimate.profitNote}</div>
                </div>
              </div>

              <div className={s.limitBox}>
                <div>
                  <div className={s.resultLabel}>예상 이용금액</div>
                  <div className={s.resultBig}>{estimate.limitLabel}</div>
                </div>
                <div className={s.limitPeriod}>
                  <div className={s.resultLabel}>예상 납부기간</div>
                  <div className={s.resultMid}>{estimate.periodLabel}</div>
                </div>
              </div>

              <div className={s.resultRow}>
                <span className={s.resultRowLabel}>
                  월 예상 납부금액
                  <Info text="예상 이용금액을 납부기간(12개월)으로 나눈 금액입니다." />
                </span>
                <span className={s.resultRowValue}>{estimate.monthPayLabel}</span>
              </div>

              <div className={s.resultRow}>
                <span className={s.resultRowLabel}>
                  월 예상 부담금액
                  <Info text="예상 이용금액에서 종합소득세 예상 절세액을 뺀 금액을 계약기간(12개월)으로 나눈 금액입니다." />
                </span>
                <span className={s.resultRowValue}>{estimate.monthBurdenLabel}</span>
              </div>

              <button
                type="button"
                className={`${p.btn} ${p.btnPrimary} ${s.quoteCta}`}
                onClick={() => onNavigate('contact')}
              >
                이 조건으로 상담신청 →
              </button>

              {/* 모바일 전용 — 상단 버튼 줄 대신 계산기 아래에 둔다 */}
              <button
                type="button"
                className={`${p.btn} ${s.quoteCta} ${s.quoteSub}`}
                onClick={() => onNavigate('about')}
                translate="no"
              >
                BNPL 알아보기 ↘
              </button>
            </div>

            <div className={s.disclaimer}>
              * 입력값에 따른 단순 예상입니다. 실제 이용금액·납부조건·비용처리 가능 여부 및 효과는
              심사, 계약조건 및 사업장별 회계·세무처리에 따라 달라질 수 있습니다.
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className={p.statBar}>
      <div className={p.statGrid}>
        {HERO_STATS.map((stat) => (
          <div className={p.statCell} key={stat.label}>
            <div className={p.statValue} translate={noTranslate(stat.value)}>{stat.value}</div>
            <div className={p.statLabel} translate={noTranslate(stat.label)}>{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Hero;
